import { useState, useRef, useMemo } from 'react';
import { Box, TextField, IconButton, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatPanel from './ChatPanel';
import { useChat } from '../hooks/useChat';
import { Message } from '../types/chat';
import { useParams } from 'react-router-dom';
import { analytics } from '../services/analytics';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../contexts/AuthContext';

// Welcome message that will always appear first
const WELCOME_MESSAGE: Message = {
  name: 'CribCritic.ai',
  message: 'Welcome to CribCritic AI! I can help you evaluate this property based on the photos. You can ask me about:\n\n• Specific rooms or areas\n• Potential repairs or maintenance issues\n• Safety hazards or concerns\n• Improvement opportunities\n• Overall property assessment\n\nHow can I help you today?',
  justification: 'left'
};

function ChatPanelHome() {
  const { 
    messages, 
    isLoading, 
    isLoadingMessages, 
    sendMessage 
  } = useChat();
  
  const { homeId } = useParams<{ homeId: string }>();
  const { user } = useAuth();
  
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Log chat_view_open analytics event
  useAnalytics(
    () => {
      if (user?.uuid && homeId) {
        analytics.logChatViewOpen(user.uuid, homeId);
      }
    },
    [homeId, user?.uuid]
  );
  
  // Always prepend the welcome message to the messages array
  const displayMessages = useMemo(() => {
    return [WELCOME_MESSAGE, ...messages];
  }, [messages]);
  
  const handleSendMessage = async () => {
    if (inputText.trim()) {
      const messageToSend = inputText;
      setInputText('');
      await sendMessage(messageToSend);
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%', 
        width: '100%',
        position: 'relative',
        flexGrow: 1
      }}
    >
      {isLoadingMessages ? (
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Messages container - THIS WILL SCROLL */}
          <Box 
            sx={{ 
              flex: 1,
              overflow: 'auto',
              display: 'flex', 
              flexDirection: 'column',
              pb: 0
            }}
          >
            <ChatPanel 
              messages={displayMessages}
              onAddMessage={handleSendMessage}
              isLoading={isLoading}
              hideInput={true}
              sx={{ flexGrow: 1, boxShadow: 'none' }} 
            />
            <div ref={messagesEndRef} />
          </Box>
          
          {/* Input area - FIXED at bottom */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.paper'
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type your message..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              size="small"
              sx={{ 
                mr: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': {
                    transition: 'border-color 0.8s ease-in-out',
                  },
                  transition: 'box-shadow 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: '0 0 5px 1px rgba(25, 118, 210, 0.2)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 0 12px 3px rgba(25, 118, 210, 0.4)',
                  },
                }
              }}
            />
            <IconButton 
              color="primary" 
              onClick={handleSendMessage}
              disabled={isLoading || !inputText.trim()}
              sx={{
                border: 'none',
                '&:focus': {
                  outline: 'none',
                },
                '&.MuiIconButton-root': {
                  boxShadow: 'none',
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );
}

export default ChatPanelHome;